<script lang="ts">
  import FormInput from '../common/FormInput.svelte';
  import FormSelect from '../common/FormSelect.svelte';
  import Button from '../common/Button.svelte';

  let {
    isOpen = $bindable(false),
    filename = '',
    suggestedBank = '',
    suggestedPerson = '',
    onConfirm = (bank: string, person: string, accountType: string, accountName: string) => {},
    onCancel = () => {}
  }: {
    isOpen?: boolean;
    filename?: string;
    suggestedBank?: string;
    suggestedPerson?: string;
    onConfirm?: (bank: string, person: string, accountType: string, accountName: string) => void;
    onCancel?: () => void;
  } = $props();

  let bank = $state(suggestedBank);
  let person = $state(suggestedPerson);
  let accountType = $state('');
  let accountName = $state('');

  const BANKS = [
    'Amex',
    'BMO',
    'Chase',
    'Fidelity',
    'SoFi',
    'US Bank',
    'Wells Fargo',
    'Bank of America',
    'Citi',
    'Discover',
    'Capital One'
  ];

  const ACCOUNT_TYPES = [
    'Checking',
    'Savings',
    'Credit Card',
    'Investment',
    'Other'
  ];

  // Update local state when props change
  $effect(() => {
    bank = suggestedBank;
    person = suggestedPerson;
    accountType = '';
    accountName = '';
  });

  function handleSubmit() {
    if (bank) {
      onConfirm(bank, person, accountType, accountName);
      isOpen = false;
    }
  }

  function handleCancel() {
    onCancel();
    isOpen = false;
  }

  function handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  }
</script>

{#if isOpen}
  <div class="modal-backdrop" onclick={handleBackdropClick}>
    <div class="modal">
      <div class="modal-header">
        <h2>Transaction Metadata</h2>
        <button class="close-button" onclick={handleCancel} aria-label="Close">×</button>
      </div>

      <div class="modal-body">
        <p class="filename">File: <strong>{filename}</strong></p>
        <p class="help-text">
          We couldn't automatically determine the bank and person for this file.
          Please provide the information below.
        </p>

        <form onsubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <FormSelect
            label="Bank"
            bind:value={bank}
            options={BANKS}
            required
            placeholder="Select a bank"
          />

          <FormInput
            label="Person (Optional)"
            type="text"
            bind:value={person}
            placeholder="e.g., Reece, Shelly"
          />

          <FormSelect
            label="Account Type (Optional)"
            bind:value={accountType}
            options={ACCOUNT_TYPES}
            placeholder="Select account type"
          />

          <FormInput
            label="Account Name (Optional)"
            type="text"
            bind:value={accountName}
            placeholder="e.g., Blue Cash, Sapphire, Checking"
          />

          <div class="button-group">
            <Button type="button" variant="secondary" onclick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!bank}>
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: var(--spacing-md);
  }

  .modal {
    background: white;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-2xl);
    max-width: 500px;
    width: 100%;
    max-height: 90vh;
    overflow: auto;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-lg);
    border-bottom: 1px solid var(--color-border);

    h2 {
      margin: 0;
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
    }

    .close-button {
      background: none;
      border: none;
      font-size: var(--font-size-3xl);
      line-height: 1;
      color: var(--color-text-tertiary);
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-sm);
      transition: all 0.2s;

      &:hover {
        background: var(--color-background-secondary);
        color: var(--color-text-primary);
      }
    }
  }

  .modal-body {
    padding: var(--spacing-lg);

    .filename {
      margin: 0 0 var(--spacing-md) 0;
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);

      strong {
        color: var(--color-text-primary);
        font-weight: var(--font-weight-medium);
      }
    }

    .help-text {
      margin: 0 0 var(--spacing-lg) 0;
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      line-height: 1.5;
    }

    form {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);
    }

    .button-group {
      display: flex;
      gap: var(--spacing-sm);
      justify-content: flex-end;
      margin-top: var(--spacing-md);
    }
  }

  @media (max-width: 640px) {
    .modal {
      max-width: 100%;
      margin: var(--spacing-md);
    }

    .modal-body .button-group {
      flex-direction: column-reverse;

      :global(button) {
        width: 100%;
      }
    }
  }
</style>
